import { __assign } from 'tslib';
import React__default, { useRef, useState, useMemo, useEffect } from 'react';
import { Box, IconButton, Typography, InputLabel, TextField, InputAdornment, alpha } from '@mui/material';
import clsx from 'clsx';
import { FocusPicker } from 'image-focus';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import DefaultIcon from '../../DefaultIcon/index.js';
import { useUserDynameekRights } from '../hooks.js';
import { isWrongCoordonate, convertPoiToPercent, convertPoiToDecimal } from './utils.js';

var PoiViewer = function PoiViewer(_a) {
    var image = _a.image, onLayoutWidthChange = _a.onLayoutWidthChange, getFocusPickerFunctions = _a.getFocusPickerFunctions, _b = _a.initialX, initialX = _b === void 0 ? 50 : _b, _c = _a.initialY, initialY = _c === void 0 ? 50 : _c, _d = _a.imageTitle, imageTitle = _d === void 0 ? "" : _d, onChange = _a.onChange, className = _a.className, _e = _a.testId, testId = _e === void 0 ? "PoiViewer" : _e, sx = _a.sx, _f = _a.KIcon, KIcon = _f === void 0 ? DefaultIcon : _f, element = _a.element;
    var focusPickerRef = useRef(null);
    var _g = useState("".concat(initialX)), x = _g[0], setX = _g[1];
    var _h = useState("".concat(initialY)), y = _h[0], setY = _h[1];
    // To manage resize image icon
    var _j = useState(), minimizeIcon = _j[0], setMinimizeIcon = _j[1];
    var _k = useState(undefined), focusPicker = _k[0], setFocusPicker = _k[1];
    var t = useTranslation("refront-components").t;
    var debouncedChangeHandler = useMemo(function () {
        return debounce(function (_a) {
            var x = _a.x, y = _a.y;
            var convertedCoordinates = convertPoiToPercent(x, y);
            setX("".concat(convertedCoordinates.x.toFixed(2)));
            setY("".concat(convertedCoordinates.y.toFixed(2)));
            if (onChange) {
                onChange(parseFloat(convertedCoordinates.x.toFixed(2)), parseFloat(convertedCoordinates.y.toFixed(2)));
            }
        }, 400);
    }, [onChange]);
    var handleChangePoi = function () {
        var convertedDecimal = convertPoiToDecimal(parseFloat(x), parseFloat(y));
        focusPicker === null || focusPicker === void 0 ? void 0 : focusPicker.setFocus({
            x: convertedDecimal.x,
            y: convertedDecimal.y
        });
    };
    useEffect(function () {
        var focusPicker;
        if (focusPickerRef.current) {
            var convertedDecimal = convertPoiToDecimal(initialX, initialY);
            focusPicker = new FocusPicker(focusPickerRef.current, {
                onChange: debouncedChangeHandler,
                focus: { x: convertedDecimal.x, y: convertedDecimal.y },
                retina: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='33' viewBox='0 0 99 100'%3E%3Ccircle r='49' cx='49' cy='51' fill='white'/%3E%3Ccircle r='39' cx='49' cy='51' fill='%230077b2'/%3E%3Ccircle r='25' cx='49' cy='51' fill='white'/%3E%3Ccircle r='10' cx='49' cy='51' fill='red'/%3E%3C/svg%3E"
            });
            setFocusPicker(focusPicker);
            // Callback to move focus picker when resize image
            if (getFocusPickerFunctions) {
                getFocusPickerFunctions(debounce(function () {
                    focusPicker.setFocus({ x: focusPicker.focus.x, y: focusPicker.focus.y });
                }, 200));
            }
        }
        return function () {
            focusPicker.disable();
            debouncedChangeHandler.cancel();
        };
    }, [debouncedChangeHandler, focusPickerRef, initialX, initialY, getFocusPickerFunctions]);
    var handleLayoutWidthChange = function () {
        setMinimizeIcon(function (old) { return !old; });
        onLayoutWidthChange && onLayoutWidthChange();
    };
    var isWrongCoordinates = isWrongCoordonate(x) || isWrongCoordonate(y);
    var isPoiUpdateAllowed = useUserDynameekRights(element).isPoiUpdateAllowed;
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(Box, { className: clsx("PoiViewer", className), "data-testid": testId, sx: __assign(__assign({}, sx), { cursor: isPoiUpdateAllowed ? "pointer" : "not-allowed" }) },
            React__default.createElement(Box, { sx: {
                    position: "relative",
                    pointerEvents: isPoiUpdateAllowed ? "auto" : "none"
                } },
                React__default.createElement(Box, null,
                    React__default.createElement("img", { src: image, alt: imageTitle, title: imageTitle, ref: focusPickerRef, style: {
                            borderRadius: "5px",
                            width: "100%"
                        } })),
                onLayoutWidthChange && (React__default.createElement(IconButton, { sx: togglerSx, "data-testid": "resizeButtonTestId", onClick: handleLayoutWidthChange, title: minimizeIcon
                        ? t("components.kf-thumbnail-generator.poi-sidebar.viewer.shrink.label")
                        : t("components.kf-thumbnail-generator.poi-sidebar.viewer.extend.label"), "aria-label": minimizeIcon
                        ? t("components.kf-thumbnail-generator.poi-sidebar.viewer.shrink.label")
                        : t("components.kf-thumbnail-generator.poi-sidebar.viewer.extend.label") },
                    React__default.createElement(KIcon, { className: minimizeIcon
                            ? "fa-solid fa-down-left-and-up-right-to-center"
                            : "fa-solid fa-up-right-and-down-left-from-center", sx: { fontSize: "medium" } }))))),
        React__default.createElement(Box, { sx: {
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "flex-start",
                flexDirection: "row",
                gap: 1.5,
                paddingTop: 1.5
            } },
            React__default.createElement(Box, { sx: {
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "row",
                    gap: 1
                } },
                React__default.createElement(Box, null,
                    React__default.createElement("svg", { width: "20", height: "33", viewBox: "0 0 99 70" },
                        React__default.createElement("circle", { r: "49", cx: "49", cy: "51", fill: "white" }),
                        React__default.createElement("circle", { r: "39", cx: "49", cy: "51", fill: "#0077b2" }),
                        React__default.createElement("circle", { r: "25", cx: "49", cy: "51", fill: "white" }),
                        React__default.createElement("circle", { r: "10", cx: "49", cy: "51", fill: "red" }))),
                React__default.createElement(Box, { sx: {
                        whiteSpace: "nowrap"
                    } },
                    React__default.createElement(Typography, { variant: "caption" }, t("components.kf-thumbnail-generator.poi-sidebar.viewer.label")))),
            React__default.createElement(Box, { sx: {
                    display: "flex",
                    flexWrap: "nowrap",
                    gap: 1
                } },
                React__default.createElement(InputLabel, { "aria-label": t("components.kf-thumbnail-generator.poi-sidebar.x.position.label"), htmlFor: "input-x" }),
                React__default.createElement(TextField, { id: "input-x", error: isWrongCoordinates, size: "small", onBlur: function () {
                        if (!isWrongCoordinates) {
                            handleChangePoi();
                        }
                    }, onChange: function (_a) {
                        var target = _a.target;
                        return setX(target.value);
                    }, value: x, "data-testid": "poiXTestId", "aria-label": t("components.kf-thumbnail-generator.poi-sidebar.x.position.label"), title: t("components.kf-thumbnail-generator.poi-sidebar.x.position.label"), InputProps: {
                        sx: inputPOIS,
                        startAdornment: (React__default.createElement(InputAdornment, { sx: inputPOIAdornmentSx, position: "start" }, "x"))
                    }, disabled: !isPoiUpdateAllowed }),
                React__default.createElement(InputLabel, { "aria-label": t("components.kf-thumbnail-generator.poi-sidebar.y.position.label"), htmlFor: "input-y" }),
                React__default.createElement(TextField, { id: "input-y", error: isWrongCoordinates, size: "small", onBlur: function () {
                        if (!isWrongCoordinates) {
                            handleChangePoi();
                        }
                    }, onChange: function (_a) {
                        var target = _a.target;
                        return setY(target.value);
                    }, value: y, "data-testid": "poiYTestId", "aria-label": t("components.kf-thumbnail-generator.poi-sidebar.y.position.label"), title: t("components.kf-thumbnail-generator.poi-sidebar.y.position.label"), InputProps: {
                        sx: inputPOIS,
                        startAdornment: (React__default.createElement(InputAdornment, { sx: inputPOIAdornmentSx, position: "start" }, "y"))
                    }, disabled: !isPoiUpdateAllowed })))));
};
var inputHeight = 35;
var inputPOIS = {
    paddingLeft: "unset",
    width: "110px",
    height: "".concat(inputHeight, "px")
};
var inputPOIAdornmentSx = {
    backgroundColor: function (theme) { return theme.palette.divider; },
    padding: "".concat(inputHeight / 2, "px 14px"),
    borderTopLeftRadius: function (theme) { return "".concat(theme.shape.borderRadius, "px"); },
    borderBottomLeftRadius: function (theme) { return "".concat(theme.shape.borderRadius, "px"); }
};
var togglerSx = {
    borderRadius: "5px",
    backgroundColor: function (theme) { return alpha(theme.palette.common.black, 0.7); },
    position: "absolute",
    top: function (theme) { return theme.spacing(1); },
    right: function (theme) { return theme.spacing(1); },
    width: "30px",
    height: "30px",
    color: function (theme) { return theme.palette.common.white; },
    "&:hover, &.Mui-focusVisible": {
        backgroundColor: function (theme) { return alpha(theme.palette.common.black, 1); }
    },
    pointerEvents: "auto"
};

export { PoiViewer as default };
//# sourceMappingURL=index.js.map
